import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { LanguagesIcon } from 'lucide-react';
import { QuoteCard } from './QuoteCard';
import { useLocale } from '../../contexts/LocaleContext';
import { useAppData } from '../../contexts/AppDataContext';
import { clockTime } from '../../utils/format';
import { cn } from '../../utils/cn';
import type { Message } from '../../types/chat';
export function MessageBubble({
  message,
  viewerRole
}: {
  message: Message;
  viewerRole: 'chef' | 'customer';
}) {
  const {
    readMessage,
    locale
  } = useLocale();
  const {
    quoteById
  } = useAppData();
  const reduce = useReducedMotion();
  const [showOriginal, setShowOriginal] = useState(false);
  const mine = message.role === viewerRole;
  const enter = reduce ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    transition: {
      duration: 0.15
    }
  } : {
    initial: {
      opacity: 0,
      y: 8,
      scale: 0.99
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    transition: {
      type: 'spring' as const,
      stiffness: 420,
      damping: 30
    }
  };
  if (message.kind === 'system') {
    return <motion.p {...enter} className="mx-auto max-w-[46ch] rounded-full bg-oat px-4 py-2 text-center text-[12px] text-ink-2" data-id="element-144" data-mp="134dkg4ahwddj">
        {message.body}
      </motion.p>;
  }
  if (message.kind === 'quote') {
    const quote = quoteById(message.quoteId);
    if (!quote) return null;
    return <div className={cn('flex', mine ? 'justify-end' : 'justify-start')} data-id="element-145" data-mp="1h44trrq2h5l7">
        <div className="w-full max-w-lg" data-id="element-146" data-mp="1m2pvjvkyvxbr">
          <QuoteCard quote={quote} role={viewerRole} data-id="element-147" data-mp="1m1o8stul9me7" />
          <p className={cn('mt-1 text-[11px] text-ink-3', mine ? 'text-right' : 'text-left')} data-id="element-148" data-mp="182ya59s4fenq">
            {clockTime(message.at, locale)}
          </p>
        </div>
      </div>;
  }
  const read = readMessage(message);
  return <div className={cn('flex', mine ? 'justify-end' : 'justify-start')} data-id="element-149" data-mp="3srurlcfr9nay">
      <motion.div {...enter} className={cn('max-w-[min(34rem,86%)]')} data-id="element-150" data-mp="p3kcgput1ic">
        <div className={cn('rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed', mine ? 'rounded-br-md bg-primary text-on-primary' : 'rounded-bl-md bg-oat text-ink')} data-id="element-151" data-mp="23bin0s9d3lum">
          {showOriginal && read.original ? read.original : read.text}
        </div>
        <div className={cn('mt-1 flex items-center gap-2 text-[11px] text-ink-3', mine ? 'justify-end' : 'justify-start')} data-id="element-152" data-mp="1omvwrvi759om">
          <span data-id="element-153" data-mp="1km2af6kek2xu">{clockTime(message.at, locale)}</span>
          {read.translatedFrom && <>
              <span className="flex items-center gap-1" data-id="element-154" data-mp="1fl4tsyf1jgyg">
                <LanguagesIcon className="h-3 w-3" aria-hidden="true" data-id="element-155" data-mp="1aml6ouopnz4i" />
                Translated from {read.translatedFrom}
              </span>
              <button type="button" onClick={() => setShowOriginal(s => !s)} className="font-medium text-primary underline underline-offset-2" data-id="element-156" data-mp="15p1qvqcd7eag">
                {showOriginal ? 'Show translation' : 'Show original'}
              </button>
            </>}
        </div>
      </motion.div>
    </div>;
}